import { useMemo } from 'react';

import { useSearchParams } from '@remix-run/react';
import type { TRootElement } from '@shared/dream-components';
import { RENDERABLE_NODE_KEYS_IN_PAGE_DATA } from '@shared/dream-components';

import type { NodeContent, Page } from '~/interfaces/page';
import type { Site, SiteVersion } from '~/interfaces/site';

import { SITE_MODAL_NODE_TYPE_BY_KEY, SiteModalSlotContext } from './nodes/context/SiteModalSlotContext';

import { serializeNode } from './nodes/serializeNode';

interface Props {
  page?: Page;
  site: Site;
}

const RENDERABLE_NODE_KEYS_IN_SITE: string[] = Object.keys(SITE_MODAL_NODE_TYPE_BY_KEY);

// Map modal node keys to their corresponding URL param values
const MODAL_PARAM_MAP: Record<string, string> = {
  signup_modal: 'signup',
  login_modal: 'login',
  recommendations_modal: 'recommendations',
};

// BEE-22175: subscriber signup-flow modals (recommendations, signup) don't
// belong on post draft previews — editors are reviewing an unpublished draft,
// not going through a subscribe journey. Draft previews are opened from Swarm
// with ?draft=true and are the only place where a signed-in editor sees the
// live Orchid site, so a stale `?modal=recommendations` in the URL (from a
// prior signup_flow persistence, a copied link, etc.) would otherwise render
// the modal on top of the draft.
const MODAL_KEYS_HIDDEN_ON_DRAFT_PREVIEW = new Set(['recommendations_modal', 'signup_modal']);

const GlobalElementsSerializer = ({ page, site }: Props) => {
  const [searchParams] = useSearchParams();
  const currentModal = searchParams.get('modal');
  const isDraftPreview = searchParams.get('draft') === 'true';

  const siteModalNodes = useMemo(
    () =>
      RENDERABLE_NODE_KEYS_IN_SITE.map((nodeKey: string) => {
        // Only render modal if the URL param matches
        if (currentModal !== MODAL_PARAM_MAP[nodeKey]) {
          return null;
        }
        if (isDraftPreview && MODAL_KEYS_HIDDEN_ON_DRAFT_PREVIEW.has(nodeKey)) {
          return null;
        }
        return site.viewable_site_version[nodeKey as keyof SiteVersion] as NodeContent;
      }).filter(Boolean) as NodeContent[],
    [site, currentModal, isDraftPreview],
  );

  const pageDataNodes = useMemo(
    () =>
      RENDERABLE_NODE_KEYS_IN_PAGE_DATA.map((nodeKey: string) => {
        if (page?.viewable_page_version?.content) {
          const node = (page.viewable_page_version.content as TRootElement)?.attrs?.data?.[nodeKey] as NodeContent;
          if (node && 'type' in node && node.type === 'doc' && node.content.length > 0) {
            return node.content[0];
          }
        }

        return null;
      }).filter(Boolean) as NodeContent[],
    [page],
  );

  return (
    <>
      <SiteModalSlotContext.Provider value>{serializeNode(siteModalNodes, '!min-h-0')}</SiteModalSlotContext.Provider>
      {serializeNode(pageDataNodes, '!min-h-0')}
    </>
  );
};

export default GlobalElementsSerializer;
